/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {error} from '../logger/logger';

/* eslint-disable ion-eslint-rules/safe-json-parse */

/**
 * Provides a safe-guard utility which ensures calls to `JSON.parse` are invoked
 * within a try / catch block, returning the parsed result if valid, otherwise
 * returns false.
 *
 * ```typescript
 *
 *   import {safeJSONParse} from '@ion/core/src/utils/JSONUtils';
 *
 *   const parsed = safeJSONParse("{\"name\":\"Asset 01, Asset 02\"}");
 *   // {name: "Asset 01, Asset 02"}
 *
 *   // return false if the provided value can not be parsed ...
 *   const parsed = safeJSONParse('malformed-json-string');
 *   // false
 *
 *   // omit returning a default value if the provided value can not be parsed ...
 *   const parsed = safeJSONParse('malformed-json-string', false);
 *   // undefined
 *
 * ```
 *
 * @param   value the json string which is to be safely parsed.
 * @returns The parsed JSON object if the given value is not malformed and does not
 *          throw, otherwise, returns false.
 */
export const safeJSONParse = (value: string): any => {
  if (value) {
    try {
      return JSON.parse(value);
    } catch (err) {
      error(`[safeJSONParse] Error Parsing JSON String: ${value}. Error: ${err?.toString()}`);
    }
  }
  return false;
};

/**
 * Provides a safe-guard utility which ensures calls to `JSON.stringify` are invoked
 * within a try / catch block, returning the stringified result if valid, otherwise
 * returns undefined.
 *
 * ```typescript
 *
 *  import {safeJSONStringify} from '@ion/core/src/utils/JSONUtils';
 *
 *   const parsed = safeJSONParse({name: "Asset 01, Asset 02"});
 *   // "{\"name\":\"Asset 01, Asset 02\"}"
 *
 * ```
 */
export const safeJSONStringify = (value: any): string | undefined => {
  if (value) {
    try {
      return JSON.stringify(value);
    } catch (err) {
      error(`[safeJSONStringify] Error JSON Stringify: ${value}. Error: ${err?.toString()}`);
    }
  }
};
